import '@/App.css';
import { Toaster } from '@/components/ui/toaster';
import { SidebarProvider } from '@/contexts/SidebarContext';
import ClubRanking from '@/pages/clubs/ClubRanking';
import Clubs from '@/pages/clubs/Clubs';
import TurnosPage from '@/pages/TurnosPage';
import Courts from '@/pages/clubs/Courts';
import { CreateClub } from '@/pages/clubs/CreateClub';
import CurrentAccount from '@/pages/CurrentAccount';
import Dashboard from '@/pages/Dashboard';
import ForgotPassword from '@/pages/ForgotPassword';
import Home from '@/pages/Home';
import Inicio from '@/pages/Inicio';
import Profile from '@/pages/Profile';
import Rankings from '@/pages/Rankings';
import Register from '@/pages/Register';
import ResetPassword from '@/pages/ResetPassword';
import TournamentGridPage from '@/pages/TournamentGridPage';
import TournamentManagement from '@/pages/TournamentManagement';
import type React from 'react';
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { ScrollToTop } from './components/ScrollToTop';
import Layout from './components/layout/Layout';
import { ImpersonationBanner } from './components/layout/ImpersonationBanner';
import { MercadoPagoOAuthCallback } from './components/mercadopago/MercadoPagoOauthCallback';
import { MercadoPagoProtectedRoute } from './components/mercadopago/MercadoPagoProtectionRoute';
import { NavigationProvider } from './contexts/NavigationContext';
import { CreateCourt } from './pages/clubs/CreateCourt';
import InviteManager from './pages/clubs/InviteManager';
import InviteInstructor from './pages/clubs/InviteInstructor';
import TournamentDetailsPage from './pages/TournamentDetailsPage';
import TVModeSelectorPage from './pages/tv/TVModeSelectorPage';
import TVModeViewPage from './pages/tv/TVModeViewPage';
import { LoginRoute } from './routes/LoginRoute';
import { ProtectedRoute } from './routes/ProtectedRoute';
import { RequireOwnerOrManager } from './routes/RequireOwnerOrManager';
import { RequirePanelAccess } from './routes/RequirePanelAccess';
import { AdminGuard } from './routes/AdminGuard';
import AdminUsuariosPage from './pages/admin/AdminUsuariosPage';
import AdminUsuarioDetallePage from './pages/admin/AdminUsuarioDetallePage';
import AdminTorneosPage from './pages/admin/AdminTorneosPage';
import AdminDeudaPage from './pages/admin/AdminDeudaPage';
import AdminTransferenciasPage from './pages/admin/AdminTransferenciasPage';
import AdminAuditoriaPage from './pages/admin/AdminAuditoriaPage';
import AdminJobsPage from './pages/admin/AdminJobsPage';
import AdminVersionesPage from './pages/admin/AdminVersionesPage';
import AdminClubsPendientesPage from './pages/admin/AdminClubsPendientesPage';
import AdminEmailTemplatesPage from './pages/admin/AdminEmailTemplatesPage';
import AdminMetricasPage from './pages/admin/AdminMetricasPage';
// Tournament Wizard V2
import { TournamentWizardPage } from './features/tournament-wizard';

const App: React.FC = () => {
  return (
    <Router>
      <ScrollToTop />
      <NavigationProvider>
        <SidebarProvider>
          <Routes>
            <Route path="/login" element={<LoginRoute />} />
            <Route path="/register" element={<Register />} />
            <Route
              path="/tv/view"
              element={
                <ProtectedRoute>
                  <RequirePanelAccess>
                    <TVModeViewPage />
                  </RequirePanelAccess>
                </ProtectedRoute>
              }
            />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route
              path="/users/password/reset-password"
              element={<ResetPassword />}
            />
            <Route
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route
                path="/"
                element={
                  <RequireOwnerOrManager>
                    <Home />
                  </RequireOwnerOrManager>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <RequireOwnerOrManager>
                    <Dashboard />
                  </RequireOwnerOrManager>
                }
              />
              <Route
                path="/tournaments"
                element={
                  <RequireOwnerOrManager>
                    <TournamentManagement />
                  </RequireOwnerOrManager>
                }
              />
              <Route
                path="/tv"
                element={
                  <RequireOwnerOrManager>
                    <TVModeSelectorPage />
                  </RequireOwnerOrManager>
                }
              />
              <Route
                path="/inicio"
                element={
                  <RequirePanelAccess>
                    <Inicio />
                  </RequirePanelAccess>
                }
              />
              <Route
                path="/profile"
                element={
                  <RequirePanelAccess>
                    <Profile />
                  </RequirePanelAccess>
                }
              />
              {/* Mis Clubes: accesible también para instructores (ven dónde dan clases) */}
              <Route
                path="/clubs"
                element={
                  <RequirePanelAccess>
                    <Clubs />
                  </RequirePanelAccess>
                }
              />
              <Route
                path="/turnos"
                element={
                  <RequirePanelAccess>
                    <TurnosPage />
                  </RequirePanelAccess>
                }
              />
              <Route
                path="/administrar-manager"
                element={
                  <RequireOwnerOrManager>
                    <InviteManager />
                  </RequireOwnerOrManager>
                }
              />
              <Route
                path="/administrar-instructor"
                element={
                  <RequirePanelAccess>
                    <InviteInstructor />
                  </RequirePanelAccess>
                }
              />
              {/* Ver detalles del club (solo-lectura): el instructor llega acá desde "Ver detalles" */}
              <Route
                path="/clubs/create"
                element={
                  <RequirePanelAccess>
                    <CreateClub />
                  </RequirePanelAccess>
                }
              />
              {/* Ver canchas (solo-lectura): el instructor llega acá desde "Gestionar canchas" */}
              <Route
                path="/clubs/:id/courts"
                element={
                  <RequirePanelAccess>
                    <Courts />
                  </RequirePanelAccess>
                }
              />
              <Route
                path="/rankings"
                element={
                  <RequirePanelAccess>
                    <Rankings />
                  </RequirePanelAccess>
                }
              />
              <Route
                path="/clubs/:id/ranking"
                element={
                  <RequirePanelAccess>
                    <ClubRanking />
                  </RequirePanelAccess>
                }
              />
              <Route
                path="/clubs/:id/courts/create"
                element={
                  <RequireOwnerOrManager>
                    <CreateCourt />
                  </RequireOwnerOrManager>
                }
              />
              {/* Tournament Wizard V2 - Main tournament creation flow */}
              <Route
                path="/crear-torneo"
                element={
                  <RequireOwnerOrManager>
                    <MercadoPagoProtectedRoute>
                      <TournamentWizardPage />
                    </MercadoPagoProtectedRoute>
                  </RequireOwnerOrManager>
                }
              />
              <Route
                path="/crear-torneo/:stepPath"
                element={
                  <RequireOwnerOrManager>
                    <MercadoPagoProtectedRoute>
                      <TournamentWizardPage />
                    </MercadoPagoProtectedRoute>
                  </RequireOwnerOrManager>
                }
              />
              <Route
                path="/torneos/:id"
                element={
                  <RequireOwnerOrManager>
                    <ProtectedRoute>
                      <TournamentDetailsPage />
                    </ProtectedRoute>
                  </RequireOwnerOrManager>
                }
              />
              <Route
                path="/torneos/:id/grilla"
                element={
                  <RequireOwnerOrManager>
                    <ProtectedRoute>
                      <TournamentGridPage />
                    </ProtectedRoute>
                  </RequireOwnerOrManager>
                }
              />
              <Route path="/oauth" element={<MercadoPagoOAuthCallback />} />
              <Route path="/cuenta-corriente" element={<CurrentAccount />} />
              <Route
                path="/admin/usuarios"
                element={
                  <AdminGuard>
                    <AdminUsuariosPage />
                  </AdminGuard>
                }
              />
              <Route
                path="/admin/usuarios/:pk"
                element={
                  <AdminGuard>
                    <AdminUsuarioDetallePage />
                  </AdminGuard>
                }
              />
              <Route
                path="/admin/torneos"
                element={
                  <AdminGuard>
                    <AdminTorneosPage />
                  </AdminGuard>
                }
              />
              <Route
                path="/admin/deuda"
                element={
                  <AdminGuard>
                    <AdminDeudaPage />
                  </AdminGuard>
                }
              />
              <Route
                path="/admin/metricas"
                element={
                  <AdminGuard>
                    <AdminMetricasPage />
                  </AdminGuard>
                }
              />
              <Route
                path="/admin/deuda/transferencias"
                element={
                  <AdminGuard>
                    <AdminTransferenciasPage />
                  </AdminGuard>
                }
              />
              <Route
                path="/admin/auditoria"
                element={
                  <AdminGuard>
                    <AdminAuditoriaPage />
                  </AdminGuard>
                }
              />
              <Route
                path="/admin/operaciones/jobs"
                element={
                  <AdminGuard>
                    <AdminJobsPage />
                  </AdminGuard>
                }
              />
              <Route
                path="/admin/operaciones/versiones"
                element={
                  <AdminGuard>
                    <AdminVersionesPage />
                  </AdminGuard>
                }
              />
              <Route
                path="/admin/operaciones/clubs"
                element={
                  <AdminGuard>
                    <AdminClubsPendientesPage />
                  </AdminGuard>
                }
              />
              <Route
                path="/admin/operaciones/emails"
                element={
                  <AdminGuard>
                    <AdminEmailTemplatesPage />
                  </AdminGuard>
                }
              />
            </Route>
          </Routes>
          {/* Above <Routes>, not inside Layout: `/tv/view` is declared as a
              top-level route outside the Layout element, and from there the
              banner would disappear along with the only way out of the support
              session. It renders nothing when there is none, so every other
              route is unaffected. */}
          <ImpersonationBanner />
        </SidebarProvider>
      </NavigationProvider>
      <Toaster />
    </Router>
  );
};

export default App;
